import React, { useEffect, useMemo, useState } from 'react';
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { Link, Redirect, useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import moment, { Moment } from "moment";
import { GoBackButton } from '@shared/ui/atoms/buttons/GoBackButton';
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import { ToggleableOutlineButton } from '@shared/ui/atoms/buttons/ToggleableOutlineButton';
import { HeadlineLarge } from '@shared/ui/atoms/fonts/Headline';
import { BodyLarge, BodyRegular, BodySmall } from '@shared/ui/atoms/fonts/Body';
import { LoadingSpinner } from '@shared/ui/atoms/LoadingSpinner';
import { Center } from '@shared/ui/atoms/StructuralLayout';
import { MountedContentCard, MountedContentCardContent } from '@shared/ui/atoms/MountedContentCard';
import { FlatContentCard } from '@shared/ui/atoms/FlatContentCard';
import { tablet } from '@shared/ui/atoms/MediaQueries';
import Colours from '@shared/ui/atoms/Colours';
import { DateRangePicker } from '@shared/ui/molecules/DateRangePicker';
import { OfficeDropDownList } from '@shared/ui/organisms/OfficeDropDownList';
import { selectCurrentUser, selectIsInTeams } from "@shared/store/auth.duck";
import { failureNotification } from "@shared/store/notification.duck";
import { IsSuperAdmin } from "@shared/hooks/IsSuperAdmin";
import { isCompanyAdminUser, isOfficeAdminUser, isProfessionalTierUser } from "@shared/utils/AccessControl";
import { DATE_FORMAT } from "@shared/utils/DateUtils";
import AccessRequired from "@features/settings/pages/components/AccessRequired";
import { selectAllOfficeEntities } from '@features/desks/store/advanceHotDeskingSetup.duck';
import { OfficeEntity } from '@features/desks/services/AdvanceHotDeskingService';
import { getOfficeHeatMap, OfficeHeatMap } from '@features/reporting/services/OfficeHeatMapService';
import { toCsvDownload } from '@features/reporting/services/ReportingService';
import { buildDateGrid, buildTypicalWeekGrid, HalfDayMode, MAX_DAY_COLUMNS, summarise, toCsvRows, TypicalWeekStat } from "./heatMapData";
import { formatPercent } from "./heatScale";
import { OccupancyHeatGrid } from "./OccupancyHeatGrid";
import { HeatMapLegend } from "./HeatMapLegend";
import { FloorHeatMapSection } from "./FloorHeatMapSection";

type View = 'TYPICAL_WEEK' | 'BY_DATE';

// Matches the server's limit
const MAX_RANGE_DAYS = 366;

function parseDate(value: string | null): Moment | undefined {
  if (!value) return undefined;
  const date = moment(value, DATE_FORMAT, true);
  return date.isValid() ? date : undefined;
}

export function OfficeHeatMapPage() {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const currentUser = useSelector(selectCurrentUser);
  const isInTeams = useSelector(selectIsInTeams);
  const offices = useSelector(selectAllOfficeEntities);
  const rootOffices = useMemo(() => offices.filter((o: OfficeEntity) => !o.parentId), [offices]);

  const isSuperAdmin = IsSuperAdmin();
  const canViewReport = isCompanyAdminUser(currentUser) || isOfficeAdminUser(currentUser);
  const hasTier = isProfessionalTierUser(currentUser);

  // The office usage page links here with its office and dates already chosen
  const initial = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const from = parseDate(params.get('from')) ?? moment().startOf('month');
    const to = parseDate(params.get('to')) ?? from.clone().endOf('month');
    const officeId = Number(params.get('officeId')) || undefined;
    const floorId = Number(params.get('floorId')) || undefined;
    return {from, to: to.isBefore(from) ? from.clone().endOf('month') : to, officeId, floorId};
    // Only read once, on arrival
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [officeId, setOfficeId] = useState<number | undefined>(initial.officeId);
  const [fromDate, setFromDate] = useState<Moment>(initial.from);
  const [toDate, setToDate] = useState<Moment>(initial.to);
  const [selectedFloorId, setSelectedFloorId] = useState<number | undefined>(initial.floorId);
  const [view, setView] = useState<View>('TYPICAL_WEEK');
  const [stat, setStat] = useState<TypicalWeekStat>('AVERAGE');
  const [mode, setMode] = useState<HalfDayMode>('FULL');
  const [report, setReport] = useState<OfficeHeatMap | undefined>();
  const [loading, setLoading] = useState(false);

  const fromKey = fromDate.format(DATE_FORMAT);
  const toKey = toDate.format(DATE_FORMAT);
  const rangeTooLong = toDate.diff(fromDate, 'days') >= MAX_RANGE_DAYS;

  useEffect(() => {
    if (!officeId && rootOffices.length > 0) {
      setOfficeId(rootOffices[0].id);
    }
  }, [officeId, rootOffices]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (officeId) params.set('officeId', `${officeId}`);
    params.set('from', fromKey);
    params.set('to', toKey);
    if (selectedFloorId) params.set('floorId', `${selectedFloorId}`);
    history.replace({search: params.toString()});
  }, [history, officeId, fromKey, toKey, selectedFloorId]);

  useEffect(() => {
    if (!isSuperAdmin || !canViewReport || !hasTier || !officeId || rangeTooLong) return;
    let cancelled = false;
    setLoading(true);
    setReport(undefined);
    getOfficeHeatMap(officeId, moment(fromKey), moment(toKey))
      .then(data => {
        if (cancelled) return;
        setReport(data);
        setSelectedFloorId(current => data.floors.some(f => f.floorId === current) ? current : undefined);
      })
      .catch(() => { if (!cancelled) dispatch(failureNotification(t('reports-page.office-usage.heat-map.load-failed'))); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isSuperAdmin, canViewReport, hasTier, officeId, fromKey, toKey, rangeTooLong, dispatch, t]);

  const grid = useMemo(() => {
    if (!report) return undefined;
    return view === 'TYPICAL_WEEK' ? buildTypicalWeekGrid(report, stat) : buildDateGrid(report, mode);
  }, [report, view, stat, mode]);

  const summary = useMemo(() => report ? summarise(report) : {}, [report]);

  const onDatesSelected = (from?: Moment, to?: Moment) => {
    if (from) setFromDate(from.clone());
    if (to) setToDate(to.clone());
  };

  // The drop down also calls this on load with the office already chosen, which must keep the floor
  const onOfficeSelected = (office: OfficeEntity) => {
    if (office.id === officeId) return;
    setOfficeId(office.id);
    setSelectedFloorId(undefined);
  };

  const onFloorSelected = (floorId: number) => {
    setSelectedFloorId(floorId);
    window.setTimeout(() => document.getElementById('floor-heat-map')?.scrollIntoView({behavior: 'smooth', block: 'start'}), 0);
  };

  const downloadCsv = () => {
    if (!report) return;
    const officePart = report.officeLabel.replace(/[^a-z0-9]+/gi, '-').toLowerCase();
    toCsvDownload(toCsvRows(report), `office-heat-map-${officePart}-${fromKey}-to-${toKey}`);
  };

  // Super admins only for now
  if (!isSuperAdmin) {
    return <Redirect to="/manage/reporting/office" />;
  }

  if (!canViewReport) {
    return (
      <ReportingContainer>
        <GoBackButton url={"/manage/reporting/office"} />
        <AccessRequired visible={true} companyAdminsRequired={true} officeAdminsRequired={true} />
      </ReportingContainer>
    );
  }

  return (
    <ReportingContainer>
      <GoBackButton url={"/manage/reporting/office"} />
      <HeadlineLarge style={{marginBottom: 8}}>{t('reports-page.office-usage.heat-map.title')}</HeadlineLarge>
      <BodySmall colour={Colours.darkGrey} style={{marginBottom: 16}}>{t('reports-page.office-usage.heat-map.description')}</BodySmall>

      {!hasTier ? (
        <EmptyState title={t('reports-page.office-usage.heat-map.upgrade-title')}>
          {t('reports-page.office-usage.heat-map.upgrade-description')}{' '}
          {isInTeams
            ? t('reports-page.office-usage.heat-map.upgrade-teams')
            : <Link to="/manage/payments">{t('reports-page.office-usage.heat-map.upgrade-link')}</Link>}
        </EmptyState>
      ) : <>
        <ControlsRow>
          <ControlGroup>
            <BodyRegular weight={600} style={{marginBottom: 8}}>{t('reports-page.office-usage.heat-map.office')}</BodyRegular>
            <OfficeDropDownList offices={rootOffices}
                                preSelectedOffice={officeId}
                                onSelect={onOfficeSelected}
                                disabled={loading} />
          </ControlGroup>
          <DateRangeGroup>
            <DateRangePicker disabled={loading}
                             onDatesSelected={onDatesSelected}
                             fromDate={fromDate}
                             toDate={toDate} />
          </DateRangeGroup>
        </ControlsRow>

        {rangeTooLong && <BodyRegular colour={Colours.red}>{t('reports-page.office-usage.heat-map.range-too-long')}</BodyRegular>}

        {loading && (
          <Center>
            <LoadingSpinner hideBorder={true} />
          </Center>
        )}

        {!loading && report && report.floors.length === 0 && (
          <EmptyState title={t('reports-page.office-usage.heat-map.no-floors-title')}>
            {t('reports-page.office-usage.heat-map.no-floors-description')}
          </EmptyState>
        )}

        {!loading && report && grid && report.floors.length > 0 && <>
          <SummaryCard>
            <SummaryTitle>{report.officeLabel}</SummaryTitle>
            <SummaryGrid>
              <SummaryItem>
                <SummaryValue>{formatPercent(summary.averageOccupancy)}</SummaryValue>
                <SummaryLabel>{t('reports-page.office-usage.heat-map.average-occupancy')}</SummaryLabel>
              </SummaryItem>
              <SummaryItem>
                <SummaryValue>{summary.busiestWeekday?.name ?? '-'}</SummaryValue>
                <SummaryLabel>{t('reports-page.office-usage.heat-map.busiest-day')}{summary.busiestWeekday ? ` (${formatPercent(summary.busiestWeekday.ratio)})` : ''}</SummaryLabel>
              </SummaryItem>
              <SummaryItem>
                <SummaryValue>{summary.quietestWeekday?.name ?? '-'}</SummaryValue>
                <SummaryLabel>{t('reports-page.office-usage.heat-map.quietest-day')}{summary.quietestWeekday ? ` (${formatPercent(summary.quietestWeekday.ratio)})` : ''}</SummaryLabel>
              </SummaryItem>
              <SummaryItem>
                <SummaryValue>{summary.peak ? formatPercent(summary.peak.ratio) : '-'}</SummaryValue>
                <SummaryLabel>
                  {t('reports-page.office-usage.heat-map.busiest-half-day')}
                  {summary.peak && <><br />{summary.peak.floorLabel}, {moment(summary.peak.date).format('ddd D MMM')} {summary.peak.period}</>}
                </SummaryLabel>
              </SummaryItem>
            </SummaryGrid>
          </SummaryCard>

          <FlatContentCard style={{marginBottom: 24}}>
            <BodyLarge weight={600} style={{marginBottom: 12}}>{t('reports-page.office-usage.heat-map.grid-title')}</BodyLarge>
            <ViewRow>
              <div>
                <BodyRegular weight={600} style={{marginBottom: 8}}>{t('reports-page.office-usage.heat-map.view-label')}</BodyRegular>
                <ToggleRow>
                  <ToggleableOutlineButton isActive={view === 'TYPICAL_WEEK'} click={() => setView('TYPICAL_WEEK')} text={'reports-page.office-usage.heat-map.typical-week'} />
                  <ToggleableOutlineButton isActive={view === 'BY_DATE'} click={() => setView('BY_DATE')} text={'reports-page.office-usage.heat-map.by-date'} />
                </ToggleRow>
              </div>
              <div>
                <BodyRegular weight={600} style={{marginBottom: 8}}>{t('reports-page.office-usage.heat-map.show-label')}</BodyRegular>
                {view === 'TYPICAL_WEEK' ? (
                  <ToggleRow>
                    <ToggleableOutlineButton isActive={stat === 'AVERAGE'} click={() => setStat('AVERAGE')} text={'reports-page.office-usage.heat-map.average'} />
                    <ToggleableOutlineButton isActive={stat === 'PEAK'} click={() => setStat('PEAK')} text={'reports-page.office-usage.heat-map.busiest'} />
                  </ToggleRow>
                ) : (
                  <ToggleRow>
                    <ToggleableOutlineButton isActive={mode === 'FULL'} click={() => setMode('FULL')} text={'reports-page.office-usage.heat-map.full-day'} />
                    <ToggleableOutlineButton isActive={mode === 'AM'} click={() => setMode('AM')} text={'reports-page.office-usage.heat-map.am'} />
                    <ToggleableOutlineButton isActive={mode === 'PM'} click={() => setMode('PM')} text={'reports-page.office-usage.heat-map.pm'} />
                  </ToggleRow>
                )}
              </div>
            </ViewRow>

            <HeatMapLegend emptyLabel="reports-page.office-usage.heat-map.legend-empty"
                           showOverCapacity={true}
                           showNoCapacity={grid.rows.some(row => row.capacity <= 0)} />
            <OccupancyHeatGrid grid={grid}
                               selectedFloorId={selectedFloorId}
                               onFloorSelected={onFloorSelected}
                               showValues={view === 'TYPICAL_WEEK'} />
            <BodySmall colour={Colours.darkGrey} style={{marginTop: 8}}>
              {t('reports-page.office-usage.heat-map.capacity-note')}
              {view === 'BY_DATE' && report.dates.length > MAX_DAY_COLUMNS && <> {t('reports-page.office-usage.heat-map.weeks-note')}</>}
            </BodySmall>
            {!selectedFloorId && (
              <BodyRegular colour={Colours.darkGrey} style={{marginTop: 16}}>{t('reports-page.office-usage.heat-map.choose-floor')}</BodyRegular>
            )}
          </FlatContentCard>

          <div id="floor-heat-map">
            {selectedFloorId && <FloorHeatMapSection floorId={selectedFloorId} fromKey={fromKey} toKey={toKey} />}
          </div>

          <FlatContentCard>
            <BodyLarge weight={600} style={{marginBottom: 4}}>{t('reports-page.office-usage.heat-map.download-title')}</BodyLarge>
            <BodyRegular colour={Colours.darkGrey} style={{marginBottom: 12}}>{t('reports-page.office-usage.heat-map.download-description')}</BodyRegular>
            <OutlineButton text="button.download-csv" click={downloadCsv} />
          </FlatContentCard>
        </>}
      </>}
    </ReportingContainer>
  );
}

function EmptyState(props: { title: string, children: React.ReactNode }) {
  return (
    <Center style={{marginTop: 32}}>
      <MountedContentCard>
        <MountedContentCardContent>
          <BodyLarge weight={600} colour={Colours.blue} style={{marginBottom: 12}}>{props.title}</BodyLarge>
          <BodyRegular>{props.children}</BodyRegular>
        </MountedContentCardContent>
      </MountedContentCard>
    </Center>
  );
}

const ReportingContainer = styled.div`
  max-width: 100%;
  min-height: 100vh;
  background-color: white;
  padding: 24px;
`

const ControlsRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
  @media (${tablet}) {
    flex-direction: row;
    align-items: flex-end;
    justify-content: space-between;
  }
`

const ControlGroup = styled.div`
  width: 100%;
  @media (${tablet}) {
    max-width: 320px;
  }
`

const DateRangeGroup = styled.div`
  width: 100%;
  .textField {
    margin-bottom: 0 !important;
  }
  @media (${tablet}) {
    max-width: 400px;
  }
`

const SummaryCard = styled.div`
  background-color: ${Colours.blue10};
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;
`

const SummaryTitle = styled.h3`
  font-family: "soleil", sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: ${Colours.black};
  margin: 0 0 16px 0;
`

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  @media (${tablet}) {
    grid-template-columns: repeat(4, 1fr);
  }
`

const SummaryItem = styled.div`
  text-align: center;
`

const SummaryValue = styled.div`
  font-family: "soleil", sans-serif;
  font-size: 28px;
  font-weight: 700;
  color: ${Colours.blue};
`

const SummaryLabel = styled.div`
  font-family: "soleil", sans-serif;
  font-size: 13px;
  color: ${Colours.darkGrey};
  margin-top: 4px;
`

const ViewRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px 32px;
  margin-bottom: 8px;
`

const ToggleRow = styled.div`
  display: flex;
  flex-direction: row;
`
